import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getApplication, listVersions, type Application, type AppVersion } from '../api/client';

function getAppIdFromPath(pathname: string): string | null {
  const m = pathname.match(/^\/panel\/applications\/([^/]+)/);
  return m ? m[1] : null;
}

/** Module-level: survives Strict Mode remounts so concurrent GETs share one HTTP call. */
const inflightApplications = new Map<string, Promise<Application>>();

function loadApplicationOnce(id: string, force = false): Promise<Application> {
  if (!force) {
    const existing = inflightApplications.get(id);
    if (existing) return existing;
  }
  const promise = getApplication(id).finally(() => {
    if (inflightApplications.get(id) === promise) {
      inflightApplications.delete(id);
    }
  });
  inflightApplications.set(id, promise);
  return promise;
}

type ApplicationContextValue = {
  app: Application | null;
  appId: string | null;
  loading: boolean;
  error: string | null;
  versions: AppVersion[];
  versionsLoading: boolean;
  refetch: () => Promise<void>;
  refetchVersions: () => Promise<void>;
  setApp: (app: Application | null) => void;
};

const ApplicationContext = createContext<ApplicationContextValue | null>(null);

const emptyFallback: ApplicationContextValue = {
  app: null,
  appId: null,
  loading: false,
  error: null,
  versions: [],
  versionsLoading: false,
  refetch: async () => {},
  refetchVersions: async () => {},
  setApp: () => {},
};

export function ApplicationProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const pathAppId = getAppIdFromPath(location.pathname);

  const [app, setAppState] = useState<Application | null>(null);
  const [appId, setAppId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [versions, setVersions] = useState<AppVersion[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);

  const setApp = useCallback((next: Application | null) => {
    setAppState(next);
    if (next) setAppId(next.id);
  }, []);

  const loadVersions = useCallback(async (id: string, cancelled?: () => boolean) => {
    setVersionsLoading(true);
    try {
      const list = await listVersions(id);
      if (!cancelled?.()) setVersions(list);
    } catch {
      if (!cancelled?.()) setVersions([]);
    } finally {
      if (!cancelled?.()) setVersionsLoading(false);
    }
  }, []);

  const fetchApp = useCallback(
    async (id: string, cancelled?: () => boolean, force = false) => {
      setLoading(true);
      setError(null);
      try {
        const data = await loadApplicationOnce(id, force);
        if (cancelled?.()) return;
        setAppState(data);
        setAppId(id);
        if (data.type === 'mobil_app') {
          await loadVersions(id, cancelled);
        } else {
          setVersions([]);
          setVersionsLoading(false);
        }
      } catch (e) {
        if (!cancelled?.()) {
          setError(e instanceof Error ? e.message : 'Yüklenemedi');
          setAppState(null);
          setAppId(null);
          setVersions([]);
          setVersionsLoading(false);
        }
      } finally {
        if (!cancelled?.()) setLoading(false);
      }
    },
    [loadVersions]
  );

  useEffect(() => {
    if (!pathAppId) {
      setAppState(null);
      setAppId(null);
      setLoading(false);
      setError(null);
      setVersions([]);
      setVersionsLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setAppState(null);
    setAppId(pathAppId);
    setVersions([]);
    void fetchApp(pathAppId, () => cancelled);
    return () => {
      cancelled = true;
    };
  }, [pathAppId, fetchApp]);

  const refetch = useCallback(async () => {
    if (pathAppId) await fetchApp(pathAppId, undefined, true);
  }, [pathAppId, fetchApp]);

  const refetchVersions = useCallback(async () => {
    if (pathAppId && app?.type === 'mobil_app') {
      await loadVersions(pathAppId);
    }
  }, [pathAppId, app?.type, loadVersions]);

  const value: ApplicationContextValue = {
    app,
    appId,
    loading,
    error,
    versions,
    versionsLoading,
    refetch,
    refetchVersions,
    setApp,
  };

  return (
    <ApplicationContext.Provider value={value}>
      {children}
    </ApplicationContext.Provider>
  );
}

export function useApplication() {
  const ctx = useContext(ApplicationContext);
  return ctx ?? emptyFallback;
}
